import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  DialogContentText,
  Tooltip,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  DataGrid,
  GridActionsCellItem,
  GridToolbarContainer,
  GridToolbarExport,
  GridToolbarFilterButton,
  GridToolbarColumnsButton,
} from '@mui/x-data-grid';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import { departmentsAPI, companiesAPI } from '../../services/api';
import { permissionsAPI } from '../../services/api';
import { usePermissions } from '../../contexts/PermissionContext';
import { AutoCloseAlert } from '../Common';

const CustomToolbar = ({ onAdd, hasCreatePermission, hasExportPermission }) => (
  <GridToolbarContainer>
    <GridToolbarColumnsButton />
    <GridToolbarFilterButton />
    {hasExportPermission && (
      <GridToolbarExport 
        printOptions={{ disableToolbarButton: true }}
        csvOptions={{ 
          disableToolbarButton: false,
          fileName: 'Departments' // Custom filename for CSV export
        }}
      />
    )}
    {hasCreatePermission && (
      <Button startIcon={<AddIcon />} onClick={onAdd}>
        Add Department
      </Button>
    )}
  </GridToolbarContainer>
);

const Departments = () => {
  const { hasPermission } = usePermissions();
  const [departments, setDepartments] = useState([]);
  const [companies, setCompanies] = useState([]); // State to store companies for dropdown
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingDepartment, setEditingDepartment] = useState(null);
  const [viewMode, setViewMode] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false); // Track if current user is superadmin
  const [formData, setFormData] = useState({
    department_name: '',
    description: '',
    company_id: '', // Company ID for department association
    is_active: true,
  });
  const [formErrors, setFormErrors] = useState({
    department_name: '',
    company_id: '',
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [departmentToDelete, setDepartmentToDelete] = useState(null);

  // Pagination state
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [rowCount, setRowCount] = useState(0);

  const canView = hasPermission('Departments', 'view');
  const canCreate = hasPermission('Departments', 'create');
  const canUpdate = hasPermission('Departments', 'update');
  const canDelete = hasPermission('Departments', 'delete');
  const canExport = hasPermission('Departments', 'export');

  // Add function to load companies for dropdown
  const loadCompanies = async () => {
    try {
      // Fetch all active companies for dropdown selection
      const response = await companiesAPI.getDropdown();
      setCompanies(response.data.data || []);
    } catch (error) {
      console.error('Error loading companies:', error);
      setError('Failed to load companies for dropdown');
    }
  };

  // Add function to check if current user is superadmin
  const checkSuperAdminStatus = async () => {
    try {
      // Check if current user has Super Admin role to enable cross-company department management
      const response = await permissionsAPI.checkSuperAdmin();
      setIsSuperAdmin(response.data.is_superadmin || false);
    } catch (error) {
      console.error('Error checking superadmin status:', error);
      setIsSuperAdmin(false);
    }
  };

  useEffect(() => {
    if (canView) {
      loadDepartments();
      loadCompanies(); // Load companies for dropdown
      checkSuperAdminStatus(); // Check if current user is superadmin
    }
  }, [canView, paginationModel]);

  const loadDepartments = async () => {
    try {
      setLoading(true);
      const response = await departmentsAPI.getAll({
        page: paginationModel.page + 1, // Backend expects 1-based page numbers
        per_page: paginationModel.pageSize,
      });
      setDepartments(response.data.data || []);
      setRowCount(response.data.total || 0);
    } catch (error) {
      console.error('Error loading departments:', error);
      setError('Failed to load departments');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingDepartment(null);
    setViewMode(false);
    setError('');
    setFormData({
      department_name: '',
      description: '',
      company_id: isSuperAdmin ? '' : undefined, // Only set company_id for superadmin users
      is_active: true,
    });
    setFormErrors({
      department_name: '',
      company_id: '',
    });
    setDialogOpen(true);
  };

  const handleEdit = (department) => {
    setEditingDepartment(department);
    setViewMode(false);
    setError('');
    setFormData({
      department_name: department.department_name,
      description: department.description || '',
      company_id: isSuperAdmin ? (department.company_id || '') : undefined, // Only set company_id for superadmin users
      is_active: department.is_active,
    });
    setFormErrors({
      department_name: '',
      company_id: '',
    });
    setDialogOpen(true);
  };

  const handleView = (department) => {
    setEditingDepartment(department);
    setViewMode(true);
    setError('');
    setFormData({
      department_name: department.department_name,
      description: department.description || '',
      company_id: isSuperAdmin ? (department.company_id || '') : undefined, // Only set company_id for superadmin users
      is_active: department.is_active,
    });
    setFormErrors({
      department_name: '',
      company_id: '',
    });
    setDialogOpen(true);
  };

  const handleDelete = (department) => {
    setDepartmentToDelete(department);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (departmentToDelete) {
      try {
        await departmentsAPI.delete(departmentToDelete.id);
        setSuccess('Department deleted successfully');
        setDeleteDialogOpen(false);
        setDepartmentToDelete(null);
        
        // Check if we need to go back to previous page if current page becomes empty
        const currentPageStartIndex = paginationModel.page * paginationModel.pageSize;
        const totalItemsAfterDelete = rowCount - 1;
        
        if (currentPageStartIndex >= totalItemsAfterDelete && paginationModel.page > 0) {
          setPaginationModel(prev => ({ ...prev, page: prev.page - 1 }));
        } else {
        loadDepartments();
        }
      } catch (error) {
        setError('Failed to delete department');
        setDeleteDialogOpen(false);
        setDepartmentToDelete(null);
      }
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setDepartmentToDelete(null);
  };

  // Validation function for form fields
  const validateForm = () => {
    const errors = {
      department_name: '',
      company_id: '',
    };
    let isValid = true;

    // Validate department name
    if (!formData.department_name.trim()) {
      errors.department_name = 'Department name is required';
      isValid = false;
    } else if (formData.department_name.length > 100) {
      errors.department_name = 'Department name must be 100 characters or less';
      isValid = false;
    }

    // Validate company selection (only for superadmin users)
    if (isSuperAdmin && !formData.company_id) {
      errors.company_id = 'Please select a company';
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Run validation
    if (!validateForm()) {
      return;
    }

    try {
      if (editingDepartment) {
        await departmentsAPI.update(editingDepartment.id, formData);
        setSuccess('Department updated successfully');
        setDialogOpen(false);
        loadDepartments(); // Reload current page
      } else {
        await departmentsAPI.create(formData);
        setSuccess('Department created successfully');
        setDialogOpen(false);
        
        // For new departments, go to first page to see the new item
        setPaginationModel(prev => ({ ...prev, page: 0 }));
      }
    } catch (error) {
      setError((error.response && error.response.data && error.response.data.error) || 'Operation failed');
      setDialogOpen(false); // Close dialog when there's an error
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setError('');
    setEditingDepartment(null);
    setViewMode(false);
    setFormErrors({
      department_name: '',
      company_id: '',
    });
  };

  const columns = [
    { field: 'id', headerName: 'ID', width: 70 },
    { field: 'department_name', headerName: 'Department Name', width: 200 },
    {
      field: 'company_name',
      headerName: 'Company',
      width: 150,
      type: 'string',
      renderCell: (params) => (
        <Chip
          label={params.value || 'N/A'}
          size="small"
          variant="outlined"
          icon={<BusinessIcon />}
        />
      ),
    },
    { field: 'description', headerName: 'Description', width: 250 },
    {
      field: 'is_active',
      headerName: 'Status',
      width: 120,
      type: 'string',
      valueGetter: (params) => {
        // Return "Active" or "Inactive" text for filtering instead of boolean
        return params.value ? 'Active' : 'Inactive';
      },
      renderCell: (params) => {
        // Use the raw row data to get the actual boolean value
        const isActive = params.row.is_active;
        return (
          <Chip
            label={isActive ? 'Active' : 'Inactive'}
            color={isActive ? 'success' : 'error'}
            size="small"
          />
        );
      },
    },
    {
      field: 'created_at',
      headerName: 'Created',
      width: 150,
      type: 'string',
      valueGetter: (params) => {
        if (!params.value) return 'N/A';
        const date = new Date(params.value);
        if (isNaN(date.getTime())) return 'N/A';
        
        // Format date with leading zeros to show DD/MM/YYYY
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        
        return `${day}/${month}/${year}`;
      },
      sortComparator: (v1, v2) => {
        if (v1 === 'N/A' && v2 === 'N/A') return 0;
        if (v1 === 'N/A') return -1;
        if (v2 === 'N/A') return 1;
        
        // Convert DD/MM/YYYY back to Date objects for comparison
        const [day1, month1, year1] = v1.split('/').map(Number);
        const [day2, month2, year2] = v2.split('/').map(Number);
        
        const date1 = new Date(year1, month1 - 1, day1);
        const date2 = new Date(year2, month2 - 1, day2);
        
        return date1.getTime() - date2.getTime();
      },
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 150,
      getActions: (params) => {
        const actions = [];
        
        if (canView) {
          actions.push(
            <Tooltip title="View Department Details" arrow>
              <GridActionsCellItem
                icon={<ViewIcon />}
                label="View"
                onClick={() => handleView(params.row)}
              />
            </Tooltip>
          );
        }
        
        if (canUpdate) {
          actions.push(
            <Tooltip title="Edit Department" arrow>
              <GridActionsCellItem
                icon={<EditIcon />}
                label="Edit"
                onClick={() => handleEdit(params.row)}
              />
            </Tooltip>
          );
        }
        
        if (canDelete) {
          actions.push(
            <Tooltip title="Delete Department" arrow>
              <GridActionsCellItem
                icon={<DeleteIcon />}
                label="Delete"
                onClick={() => handleDelete(params.row)}
              />
            </Tooltip>
          );
        }
        
        return actions;
      },
    },
  ];

  if (!canView) {
    return (
      <Box p={3}>
        <Alert severity="error">
          You don't have permission to view departments.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Departments Management
      </Typography>
      
      <AutoCloseAlert
        severity="error"
        message={error}
        show={!!error}
        onClose={() => setError('')}
        autoCloseDelay={6000}
      />
      
      <AutoCloseAlert
        severity="success"
        message={success}
        show={!!success}
        onClose={() => setSuccess('')}
        autoCloseDelay={4000}
      />

      <Paper sx={{ height: 'auto', width: '100%' }}>
        <DataGrid
          rows={departments}
          columns={columns}
          loading={loading}
          rowCount={rowCount}
          paginationModel={paginationModel}
          paginationMode="server"
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[5, 10, 25, 50, 100]}
          disableSelectionOnClick
          components={{
            Toolbar: () => <CustomToolbar onAdd={handleAdd} hasCreatePermission={canCreate} hasExportPermission={canExport} />
          }}
        />
      </Paper>

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {viewMode ? 'View Department' : editingDepartment ? 'Edit Department' : 'Add New Department'}
        </DialogTitle>
        <form onSubmit={handleSubmit} noValidate>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Department Name"
              fullWidth
              variant="outlined"
              value={formData.department_name}
              onChange={(e) => {
                const value = e.target.value;
                setFormData({ ...formData, department_name: value });
                // Clear error when user starts typing
                if (formErrors.department_name) {
                  setFormErrors({ ...formErrors, department_name: '' });
                }
              }}
              onBlur={() => {
                // Validate on blur
                if (!formData.department_name.trim()) {
                  setFormErrors({ ...formErrors, department_name: 'Department name is required' });
                } else if (formData.department_name.length > 100) {
                  setFormErrors({ ...formErrors, department_name: 'Department name must be 100 characters or less' });
                }
              }}
              disabled={viewMode}
              sx={{ mb: 2 }}
              helperText={formErrors.department_name || "Enter a unique department name (max 100 characters)"}
              error={!!formErrors.department_name}
              inputProps={{ maxLength: 100 }}
              FormHelperTextProps={{
                sx: {
                  color: formErrors.department_name ? 'error.main' : 'text.secondary',
                  fontWeight: formErrors.department_name ? 500 : 'normal'
                }
              }}
            />

            {/* Company dropdown positioned below department name */}
            {/* Only show company dropdown for superadmin users */}
            {isSuperAdmin && (
              <FormControl fullWidth variant="outlined" sx={{ mb: 2 }} error={!!formErrors.company_id}>
                <InputLabel id="company-select-label">
                  Company
                </InputLabel>
                <Select
                  labelId="company-select-label"
                  id="company-select"
                  value={formData.company_id}
                  onChange={(e) => {
                    const value = e.target.value;
                    setFormData({ ...formData, company_id: value });
                    // Clear error when user selects a company
                    if (formErrors.company_id) {
                      setFormErrors({ ...formErrors, company_id: '' });
                    }
                  }}
                  onBlur={() => {
                    // Validate on blur
                    if (!formData.company_id) {
                      setFormErrors({ ...formErrors, company_id: 'Please select a company' });
                    }
                  }}
                  label="Company"
                  disabled={viewMode}
                >
                  <MenuItem value="">
                    <em>Select Company</em>
                  </MenuItem>
                  {/* Render company options for dropdown selection */}
                  {companies.map((company) => (
                    <MenuItem key={company.id} value={company.id}>
                      {company.company_name}
                    </MenuItem>
                  ))}
                </Select>
                {formErrors.company_id && (
                  <Box sx={{ 
                    color: 'error.main', 
                    fontSize: '0.75rem', 
                    mt: 0.5, 
                    ml: 1.75,
                    fontWeight: 500
                  }}>
                    {formErrors.company_id}
                  </Box>
                )}
              </FormControl>
            )}

            <TextField
              margin="dense"
              label="Description"
              fullWidth
              variant="outlined"
              multiline
              rows={3}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              disabled={viewMode}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  disabled={viewMode}
                />
              }
              label="Active Status"
              sx={{ mt: 1 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>
              {viewMode ? 'Close' : 'Cancel'}
            </Button>
            {!viewMode && (
              <Button type="submit" variant="contained">
                {editingDepartment ? 'Update' : 'Create'}
              </Button>
            )}
          </DialogActions>
        </form>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ 
          color: 'error.main', 
          fontWeight: 'bold',
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <DeleteIcon />
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: '1.1rem', color: 'text.primary' }}>
            Are you sure you want to delete the department{' '}
            <strong>{departmentToDelete?.department_name}</strong>?
          </DialogContentText>
          <DialogContentText sx={{ mt: 2, color: 'text.secondary' }}>
            This action cannot be undone. The department will be permanently removed from the system.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button 
            onClick={handleCancelDelete}
            variant="outlined"
            size="large"
            sx={{ minWidth: 100 }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
            size="large"
            sx={{ minWidth: 100 }}
            autoFocus
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Departments; 